import React, { useEffect, useState, useCallback } from 'react';
import { validateCPF, validateCNPJ, validateCNH, maskCPF, maskCNPJ, maskCNH, maskPhone, fetchCNPJ, mapCNPJData } from '../utils/validators';
import { Search, Plus, Pencil, CheckCircle, XCircle, Eye, Truck } from 'lucide-react';
import toast from 'react-hot-toast';
import CrudPage from '../components/CrudPage';
import { Modal, FormGroup, Input, Select, Textarea, Badge, Table, EmptyState, PageHeader, Loading } from '../components/UI';
import api from '../services/api';
import {
  clientService, assetService, driverService, vehicleService,
  tenderService, contractService, logisticsService, maintenanceService,
  financialService, invoiceService, reportsService
} from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
const fmtMoney = (v) => v != null ? `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';

const STATUS_LABELS = {
  disponivel: 'Disponível', locado: 'Locado', manutencao: 'Manutenção', inativo: 'Inativo',
  ativo: 'Ativo', encerrado: 'Encerrado', suspenso: 'Suspenso', pendente: 'Pendente',
  pago: 'Pago', atrasado: 'Atrasado', cancelado: 'Cancelado',
  agendado: 'Agendado', em_rota: 'Em Rota', concluido: 'Concluído',
  avulso: 'Avulso', recorrente: 'Recorrente', licitacao: 'Licitação',
  entrega: 'Entrega', coleta: 'Coleta', limpeza: 'Limpeza',
  preventiva: 'Preventiva', corretiva: 'Corretiva', higienizacao: 'Higienização',
  receita: 'Receita', despesa: 'Despesa',
  fisica: 'Pessoa Física', juridica: 'Pessoa Jurídica',
};

const lbl = (v) => STATUS_LABELS[v] || v;

// ─── Clients ──────────────────────────────────────────────────────────────────

export function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docStatus, setDocStatus] = useState('');
  const emptyForm = { person_type: 'juridica', name: '', document: '', email: '', phone: '', contact_name: '', zip_code: '', street: '', number: '', neighborhood: '', city: '', state: '', notes: '' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await clientService.list({ search: search||undefined, limit:200 }); setItems(Array.isArray(r)?r:r.data??[]); }
    catch { toast.error('Erro ao carregar clientes'); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDocStatus(''); setModalOpen(true); };
  const openEdit = (i) => { setEditing(i); setForm({ ...emptyForm, ...i }); setDocStatus(''); setModalOpen(true); };

  const handleDocument = async (e) => {
    const raw = e.target.value.replace(/\D/g,'');
    const isPJ = form.person_type === 'juridica';
    const masked = isPJ ? maskCNPJ(e.target.value) : maskCPF(e.target.value);
    setForm(f => ({ ...f, document: masked }));

    if (isPJ && raw.length === 14) {
      if (!validateCNPJ(raw)) { setDocStatus('invalid'); toast.error('CNPJ inválido'); return; }
      setDocStatus('loading');
      try {
        const data = await fetchCNPJ(raw);
        const mapped = mapCNPJData(data);
        setForm(f => ({ ...f, document: masked, name: mapped.company_name || f.name, email: mapped.email || f.email, phone: mapped.phone || f.phone, zip_code: mapped.zip_code || f.zip_code, street: mapped.address || f.street, city: mapped.city || f.city, state: mapped.state || f.state }));
        setDocStatus('ok'); toast.success('Dados preenchidos pela Receita Federal!');
      } catch { setDocStatus('error'); }
    } else if (!isPJ && raw.length === 11) {
      if (!validateCPF(raw)) { setDocStatus('invalid'); toast.error('CPF inválido'); return; }
      setDocStatus('ok');
    } else { setDocStatus(''); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (docStatus === 'invalid') return toast.error('Corrija o CPF/CNPJ');
    setSaving(true);
    try {
      if (editing) { await clientService.update(editing.id, form); toast.success('Cliente atualizado!'); }
      else { await clientService.create(form); toast.success('Cliente criado!'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const docHint = { loading:'🔄 Consultando Receita Federal...', ok: form.person_type==='juridica'?'✅ CNPJ válido':'✅ CPF válido', error:'⚠️ CNPJ válido, sem dados na Receita', invalid: form.person_type==='juridica'?'❌ CNPJ inválido':'❌ CPF inválido' }[docStatus];
  const docBorder = { ok:'#22c55e', error:'#f59e0b', invalid:'#ef4444' }[docStatus];

  const columns = [
    { key:'name', label:'Nome / Razão Social' },
    { key:'person_type', label:'Tipo', render: i => <Badge status={i.person_type}>{lbl(i.person_type)}</Badge> },
    { key:'document', label:'CPF/CNPJ' },
    { key:'city', label:'Cidade', render: i => i.city ? `${i.city}/${i.state}` : '-' },
    { key:'phone', label:'Telefone', render: i => i.phone||'-' },
    { key:'status', label:'Status', render: i => <Badge status={i.is_active?'ativo':'inativo'}>{i.is_active?'Ativo':'Inativo'}</Badge> },
    { key:'__actions', label:'', render: i => (
      <div className="table-actions">
        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(i)}>Editar</button>
      </div>
    )},
  ];

  return (
    <>
      <PageHeader title="Clientes" subtitle={`${items.length} registros`}>
        <button className="btn btn-primary" onClick={openCreate}>+ Novo</button>
      </PageHeader>
      <div className="page-content"><div className="card">
        <div className="card-header">
          <input className="search-input" placeholder="Buscar..." value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        {!loading && items.length===0 ? <EmptyState title="Nenhum cliente cadastrado" /> : <Table columns={columns} data={items} loading={loading} />}
      </div></div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Cliente' : 'Novo Cliente'}>
        <form onSubmit={save}>
          <div className="form-grid-2">
            <FormGroup label="Tipo de Pessoa" required>
              <Select value={form.person_type} onChange={e => { setForm(f=>({...f,person_type:e.target.value,document:''})); setDocStatus(''); }}>
                <option value="juridica">Jurídica (CNPJ)</option>
                <option value="fisica">Física (CPF)</option>
              </Select>
            </FormGroup>
            <FormGroup label={form.person_type==='juridica'?'CNPJ':'CPF'} required>
              <Input value={form.document} onChange={handleDocument}
                placeholder={form.person_type==='juridica'?'00.000.000/0001-00':'000.000.000-00'}
                style={docBorder ? {borderColor: docBorder} : {}}
                maxLength={form.person_type==='juridica'?18:14} />
              {docHint && <small style={{color: docStatus==='invalid'?'#ef4444':'#6b7280',fontSize:12}}>{docHint}</small>}
            </FormGroup>
            <FormGroup label="Nome / Razão Social" required>
              <Input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} required />
            </FormGroup>
            <FormGroup label="Nome do Contato">
              <Input value={form.contact_name} onChange={e=>setForm(f=>({...f,contact_name:e.target.value}))} />
            </FormGroup>
            <FormGroup label="E-mail">
              <Input type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} />
            </FormGroup>
            <FormGroup label="Telefone">
              <Input value={form.phone} onChange={e=>setForm(f=>({...f,phone:maskPhone(e.target.value)}))} placeholder="(00) 00000-0000" maxLength={15} />
            </FormGroup>
            <FormGroup label="CEP">
              <Input value={form.zip_code} onChange={e=>setForm(f=>({...f,zip_code:e.target.value}))} />
            </FormGroup>
            <FormGroup label="Número">
              <Input value={form.number} onChange={e=>setForm(f=>({...f,number:e.target.value}))} />
            </FormGroup>
          </div>
          <FormGroup label="Logradouro">
            <Input value={form.street} onChange={e=>setForm(f=>({...f,street:e.target.value}))} />
          </FormGroup>
          <div className="form-grid-3">
            <FormGroup label="Bairro">
              <Input value={form.neighborhood} onChange={e=>setForm(f=>({...f,neighborhood:e.target.value}))} />
            </FormGroup>
            <FormGroup label="Cidade">
              <Input value={form.city} onChange={e=>setForm(f=>({...f,city:e.target.value}))} />
            </FormGroup>
            <FormGroup label="UF">
              <Input value={form.state} onChange={e=>setForm(f=>({...f,state:e.target.value}))} maxLength={2} />
            </FormGroup>
          </div>
          <FormGroup label="Observações">
            <Textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
          </FormGroup>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving||docStatus==='invalid'||docStatus==='loading'}>
              {saving?'Salvando...':'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

// ─── Assets ───────────────────────────────────────────────────────────────────

export function Assets() {
  const handleQR = async (asset) => {
    try { await reportsService.assetQRCode(asset.id, asset.code); toast.success('QR Code baixado!'); }
    catch { toast.error('Erro ao gerar QR Code'); }
  };
  const handleAllQR = async () => {
    try { await reportsService.allQRCodesPDF(); toast.success('PDF com QR Codes gerado!'); }
    catch { toast.error('Erro ao gerar PDF'); }
  };
  const handlePDF = async () => {
    try { await reportsService.assetsPDF(); toast.success('Relatório PDF gerado!'); }
    catch { toast.error('Erro ao gerar PDF'); }
  };
  const handleExcel = async () => {
    try { await reportsService.assetsExcel(); toast.success('Excel gerado!'); }
    catch { toast.error('Erro ao gerar Excel'); }
  };

  return (
    <CrudPage
      title="Banheiros"
      service={assetService}
      searchPlaceholder="Buscar por código ou modelo..."
      columns={[
        { header: 'Código', key: 'code' },
        { header: 'Tipo', render: i => <Badge status="info" label={lbl(i.asset_type)} /> },
        { header: 'Marca / Modelo', render: i => [i.brand, i.model].filter(Boolean).join(' ') || '-' },
        { header: 'Status', render: i => <Badge status={i.status} label={lbl(i.status)} /> },
        { header: 'Localização', render: i => i.current_address || '-' },
      ]}
      formFields={[
        { key: 'code', label: 'Código', required: true },
        { key: 'asset_type', label: 'Tipo', type: 'select', required: true, options: [
          { value: 'standard', label: 'Standard' },
          { value: 'luxo', label: 'Luxo' },
          { value: 'pne', label: 'PNE (Acessível)' },
          { value: 'com_lavatorio', label: 'Com Lavatório' },
          { value: 'container', label: 'Container Sanitário' },
        ]},
        { key: 'brand', label: 'Marca' },
        { key: 'model', label: 'Modelo' },
        { key: 'capacity_liters', label: 'Capacidade (L)', type: 'number' },
        { key: 'year', label: 'Ano', type: 'number' },
        { key: 'status', label: 'Status', type: 'select', default: 'disponivel', options: [
          { value: 'disponivel', label: 'Disponível' },
          { value: 'locado', label: 'Locado' },
          { value: 'manutencao', label: 'Manutenção' },
          { value: 'inativo', label: 'Inativo' },
        ]},
        { key: 'acquisition_date', label: 'Data de Aquisição', type: 'date' },
        { key: 'acquisition_value', label: 'Valor de Aquisição', type: 'number' },
        { key: 'notes', label: 'Observações', type: 'textarea', full: true },
      ]}
      getLabel={i => i.code}
      extraActions={(item) => (
        <button className="btn-icon" title="QR Code" onClick={() => handleQR(item)} style={{ color: '#6366f1' }}>
          📱
        </button>
      )}
      headerActions={
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={handleAllQR} title="PDF com todos os QR Codes">📋 QR Codes</button>
          <button className="btn btn-secondary btn-sm" onClick={handlePDF} title="Relatório PDF">📄 PDF</button>
          <button className="btn btn-secondary btn-sm" onClick={handleExcel} title="Exportar Excel">📊 Excel</button>
        </div>
      }
    />
  );
}

// ─── Drivers ──────────────────────────────────────────────────────────────────

export function Drivers() {
  const today = new Date();
  const warn30 = (d) => {
    if (!d) return false;
    const diff = (new Date(d) - today) / (1000*60*60*24);
    return diff <= 30 && diff >= 0;
  };

  return (
    <CrudPage
      title="Motoristas"
      service={driverService}
      columns={[
        { header: 'Nome', key: 'name' },
        { header: 'CPF', key: 'cpf' },
        { header: 'CNH', render: i => `${i.cnh} (${i.cnh_category || '-'})` },
        { header: 'Venc. CNH', render: i => (
          <span style={{ color: warn30(i.cnh_expiry) ? 'var(--warning)' : undefined }}>
            {fmtDate(i.cnh_expiry)}
          </span>
        )},
        { header: 'ASO', render: i => (
          <span style={{ color: warn30(i.aso_expiry) ? 'var(--warning)' : undefined }}>
            {fmtDate(i.aso_expiry)}
          </span>
        )},
        { header: 'Status', render: i => <Badge status={i.status} label={lbl(i.status)} /> },
      ]}
      formFields={[
        { key: 'name', label: 'Nome Completo', required: true, full: true },
        { key: 'cpf', label: 'CPF (validado)', required: true },
        { key: 'cnh', label: 'CNH (validada)', required: true },
        { key: 'cnh_category', label: 'Categoria CNH' },
        { key: 'cnh_expiry', label: 'Vencimento CNH', type: 'date' },
        { key: 'aso_date', label: 'Data ASO', type: 'date' },
        { key: 'aso_expiry', label: 'Vencimento ASO', type: 'date' },
        { key: 'phone', label: 'Telefone' },
        { key: 'email', label: 'E-mail', type: 'email' },
        { key: 'status', label: 'Status', type: 'select', default: 'ativo', options: [
          { value: 'ativo', label: 'Ativo' },
          { value: 'inativo', label: 'Inativo' },
          { value: 'afastado', label: 'Afastado' },
        ]},
        { key: 'notes', label: 'Observações', type: 'textarea', full: true },
      ]}
      getLabel={i => i.name}
    />
  );
}

// ─── Vehicles ─────────────────────────────────────────────────────────────────

export function Vehicles() {
  return (
    <CrudPage
      title="Veículos"
      service={vehicleService}
      columns={[
        { header: 'Placa', key: 'plate' },
        { header: 'Marca / Modelo', render: i => [i.brand, i.model, i.year].filter(Boolean).join(' ') },
        { header: 'Carga (kg)', render: i => i.payload_kg || '-' },
        { header: 'CRLV', render: i => fmtDate(i.crlv_expiry) },
        { header: 'Seguro', render: i => fmtDate(i.insurance_expiry) },
        { header: 'Status', render: i => <Badge status={i.status} label={lbl(i.status)} /> },
      ]}
      formFields={[
        { key: 'plate', label: 'Placa', required: true },
        { key: 'brand', label: 'Marca' },
        { key: 'model', label: 'Modelo' },
        { key: 'year', label: 'Ano', type: 'number' },
        { key: 'color', label: 'Cor' },
        { key: 'payload_kg', label: 'Capacidade de Carga (kg)', type: 'number' },
        { key: 'crlv_expiry', label: 'Vencimento CRLV', type: 'date' },
        { key: 'insurance_expiry', label: 'Vencimento Seguro', type: 'date' },
        { key: 'insurance_policy', label: 'Apólice do Seguro' },
        { key: 'status', label: 'Status', type: 'select', default: 'ativo', options: [
          { value: 'ativo', label: 'Ativo' },
          { value: 'manutencao', label: 'Manutenção' },
          { value: 'inativo', label: 'Inativo' },
        ]},
        { key: 'km_current', label: 'KM Atual', type: 'number' },
        { key: 'notes', label: 'Observações', type: 'textarea', full: true },
      ]}
      getLabel={i => i.plate}
    />
  );
}

// ─── Tenders ──────────────────────────────────────────────────────────────────

export function Tenders() {
  return (
    <CrudPage
      title="Licitações"
      service={tenderService}
      columns={[
        { header: 'Nº Edital', key: 'number' },
        { header: 'Órgão Público', key: 'public_agency' },
        { header: 'Vigência', render: i => `${fmtDate(i.start_date)} - ${fmtDate(i.end_date)}` },
        { header: 'Valor', render: i => fmtMoney(i.value) },
        { header: 'Status', render: i => <Badge status={i.status} label={lbl(i.status)} /> },
      ]}
      formFields={[
        { key: 'number', label: 'Nº do Edital', required: true },
        { key: 'public_agency', label: 'Órgão Público', required: true, full: true },
        { key: 'description', label: 'Descrição', type: 'textarea', full: true },
        { key: 'opening_date', label: 'Data de Abertura', type: 'date' },
        { key: 'start_date', label: 'Início da Vigência', type: 'date' },
        { key: 'end_date', label: 'Fim da Vigência', type: 'date' },
        { key: 'value', label: 'Valor (R$)', type: 'number' },
        { key: 'status', label: 'Status', type: 'select', default: 'em_andamento', options: [
          { value: 'em_andamento', label: 'Em Andamento' },
          { value: 'vencido', label: 'Vencido' },
          { value: 'encerrado', label: 'Encerrado' },
          { value: 'cancelado', label: 'Cancelado' },
        ]},
        { key: 'notes', label: 'Observações', type: 'textarea', full: true },
      ]}
      getLabel={i => i.number}
    />
  );
}

// ─── Contracts ────────────────────────────────────────────────────────────────

export function Contracts() {
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ client_id: '', contract_type: 'recorrente', start_date: '', unit_value: '', billing_day: 1, cleaning_frequency_days: 7, delivery_address: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientService.list({ limit: 500 }).then(r => setClients(r.data));
    load();
  }, []);

  const load = () => {
    setLoading(true);
    contractService.list({ limit: 200 }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contractService.create({ ...form, items: [] });
      toast.success('Contrato criado!');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter(i =>
    !search || i.number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <PageHeader title="Contratos">
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Novo Contrato</button>
      </PageHeader>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div className="search-wrap">
              <Search size={16} />
              <input className="search-input" placeholder="Buscar por número..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>{filtered.length} registros</span>
          </div>
          <Table headers={['Número','Tipo','Status','Início','Fim','Valor Unit.','Limpeza','Ações']} loading={loading}>
            {filtered.map(i => (
              <tr key={i.id}>
                <td><strong>{i.number}</strong></td>
                <td><Badge status={i.contract_type} label={lbl(i.contract_type)} /></td>
                <td><Badge status={i.status} label={lbl(i.status)} /></td>
                <td>{fmtDate(i.start_date)}</td>
                <td>{fmtDate(i.end_date)}</td>
                <td>{fmtMoney(i.unit_value)}</td>
                <td>—</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-icon" title="Baixar PDF" onClick={async () => {
                      try { await reportsService.contractPDF(i.id, i.number); toast.success('PDF gerado!'); }
                      catch { toast.error('Erro ao gerar PDF'); }
                    }} style={{ color: '#6366f1' }}>📄</button>
                    <button className="btn-icon" title="Assinatura Digital" style={{ color: i.signed_at ? '#22c55e' : '#f59e0b' }}
                      onClick={async () => {
                        try {
                          const r = await api.post('/signatures/send', { contract_id: i.id });
                          await navigator.clipboard.writeText(r.data.signature_link);
                          toast.success('Link de assinatura copiado! Envie para o cliente.');
                        } catch (err) {
                          if (err.response?.data?.detail?.includes('já assinado')) toast.success('✅ Contrato já assinado!');
                          else toast.error('Erro ao gerar link');
                        }
                      }}>
                      {i.signed_at ? '✅' : '✍️'}
                    </button>
                    <button className="btn-icon" onClick={() => {
                      contractService.update(i.id, { status: i.status === 'ativo' ? 'encerrado' : 'ativo' })
                        .then(() => { toast.success('Status atualizado'); load(); });
                    }} title={i.status === 'ativo' ? 'Encerrar' : 'Ativar'}>
                      {i.status === 'ativo' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Novo Contrato"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Criar'}</button>
        </>}>
        <form onSubmit={save}>
          <FormGroup label="Cliente" required>
            <Select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required>
              <option value="">Selecione...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormGroup>
          <div className="form-row">
            <FormGroup label="Tipo" required>
              <Select value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})}>
                <option value="avulso">Avulso</option>
                <option value="recorrente">Recorrente</option>
                <option value="licitacao">Licitação</option>
              </Select>
            </FormGroup>
            <FormGroup label="Início" required>
              <Input type="date" required value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
            </FormGroup>
          </div>
          <div className="form-row">
            <FormGroup label="Valor Unitário (R$)" required>
              <Input type="number" step="0.01" required value={form.unit_value} onChange={e => setForm({...form, unit_value: e.target.value})} />
            </FormGroup>
            <FormGroup label="Dia de Faturamento">
              <Input type="number" min={1} max={28} value={form.billing_day} onChange={e => setForm({...form, billing_day: e.target.value})} />
            </FormGroup>
          </div>
          <FormGroup label="Frequência de Limpeza (dias)">
            <Input type="number" value={form.cleaning_frequency_days} onChange={e => setForm({...form, cleaning_frequency_days: e.target.value})} />
          </FormGroup>
          <FormGroup label="Endereço de Entrega">
            <Input value={form.delivery_address} onChange={e => setForm({...form, delivery_address: e.target.value})} />
          </FormGroup>
          <FormGroup label="Observações">
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </FormGroup>
        </form>
      </Modal>
    </>
  );
}

// ─── Logistics ────────────────────────────────────────────────────────────────

export function Logistics() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ logistics_type: 'entrega', scheduled_date: '', driver_id: '', vehicle_id: '', address: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    driverService.list({ limit: 200 }).then(r => setDrivers(r.data));
    vehicleService.list({ limit: 200 }).then(r => setVehicles(r.data));
    load();
  }, []);

  const load = () => {
    setLoading(true);
    logisticsService.list({ limit: 200 }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await logisticsService.create(form);
      toast.success('Operação agendada!');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    } finally {
      setSaving(false);
    }
  };

  const doCheckin = async (id) => {
    await logisticsService.checkin(id, {});
    toast.success('Check-in registrado');
    load();
  };

  const doCheckout = async (id) => {
    await logisticsService.checkout(id, {});
    toast.success('Check-out registrado');
    load();
  };

  const filtered = items.filter(i => !filter || i.status === filter);

  return (
    <>
      <PageHeader title="Logística">
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Agendar</button>
      </PageHeader>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div className="filters-bar" style={{ margin: 0 }}>
              {['', 'agendado', 'em_rota', 'concluido'].map(s => (
                <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFilter(s)}>
                  {s ? lbl(s) : 'Todos'}
                </button>
              ))}
            </div>
          </div>
          <Table headers={['Tipo','Status','Data','Motorista','Veículo','Endereço','Ações']} loading={loading}>
            {filtered.map(i => (
              <tr key={i.id}>
                <td><Badge status={i.logistics_type} label={lbl(i.logistics_type)} /></td>
                <td><Badge status={i.status} label={lbl(i.status)} /></td>
                <td>{fmtDate(i.scheduled_date)}</td>
                <td>{drivers.find(d => d.id === i.driver_id)?.name || '-'}</td>
                <td>{vehicles.find(v => v.id === i.vehicle_id)?.plate || '-'}</td>
                <td>{i.address || '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {i.status === 'agendado' && (
                      <button className="btn btn-sm btn-primary" onClick={() => doCheckin(i.id)}>Check-in</button>
                    )}
                    {i.status === 'em_rota' && (
                      <button className="btn btn-sm btn-success" onClick={() => doCheckout(i.id)}>Concluir</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Agendar Operação"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Agendar'}</button>
        </>}>
        <form onSubmit={save}>
          <div className="form-row">
            <FormGroup label="Tipo" required>
              <Select value={form.logistics_type} onChange={e => setForm({...form, logistics_type: e.target.value})}>
                <option value="entrega">Entrega</option>
                <option value="coleta">Coleta</option>
                <option value="limpeza">Limpeza</option>
                <option value="manutencao">Manutenção</option>
              </Select>
            </FormGroup>
            <FormGroup label="Data/Hora" required>
              <Input type="datetime-local" required value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} />
            </FormGroup>
          </div>
          <div className="form-row">
            <FormGroup label="Motorista">
              <Select value={form.driver_id} onChange={e => setForm({...form, driver_id: e.target.value})}>
                <option value="">Selecione...</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </FormGroup>
            <FormGroup label="Veículo">
              <Select value={form.vehicle_id} onChange={e => setForm({...form, vehicle_id: e.target.value})}>
                <option value="">Selecione...</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.plate} - {v.model}</option>)}
              </Select>
            </FormGroup>
          </div>
          <FormGroup label="Endereço">
            <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
          </FormGroup>
          <FormGroup label="Observações">
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
          </FormGroup>
        </form>
      </Modal>
    </>
  );
}

// ─── Maintenance ──────────────────────────────────────────────────────────────

export function Maintenance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ asset_id: '', maintenance_type: 'preventiva', scheduled_date: '', responsible: '', cost: '', description: '', next_maintenance: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    assetService.list({ limit: 500 }).then(r => setAssets(r.data));
    load();
  }, []);

  const load = () => {
    setLoading(true);
    maintenanceService.list({ limit: 200 }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await maintenanceService.create(form);
      toast.success('Manutenção registrada!');
      setModalOpen(false);
      load();
    } catch (err) { toast.error('Erro'); } finally { setSaving(false); }
  };

  const complete = async (id) => {
    await maintenanceService.update(id, { status: 'concluido', completed_date: new Date().toISOString().split('T')[0] });
    toast.success('Manutenção concluída!');
    load();
  };

  return (
    <>
      <PageHeader title="Manutenção">
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Registrar</button>
      </PageHeader>
      <div className="page-content">
        <div className="card">
          <Table headers={['Banheiro','Tipo','Data Prog.','Responsável','Custo','Status','Ações']} loading={loading}>
            {items.map(i => (
              <tr key={i.id}>
                <td>{assets.find(a => a.id === i.asset_id)?.code || i.asset_id?.slice(0,8)}</td>
                <td><Badge status={i.maintenance_type} label={lbl(i.maintenance_type)} /></td>
                <td>{fmtDate(i.scheduled_date)}</td>
                <td>{i.responsible || '-'}</td>
                <td>{fmtMoney(i.cost)}</td>
                <td><Badge status={i.status} label={lbl(i.status)} /></td>
                <td>
                  {i.status !== 'concluido' && (
                    <button className="btn btn-sm btn-success" onClick={() => complete(i.id)}>Concluir</button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Registrar Manutenção"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </>}>
        <form onSubmit={save}>
          <FormGroup label="Banheiro" required>
            <Select value={form.asset_id} onChange={e => setForm({...form, asset_id: e.target.value})} required>
              <option value="">Selecione...</option>
              {assets.map(a => <option key={a.id} value={a.id}>{a.code} - {lbl(a.asset_type)}</option>)}
            </Select>
          </FormGroup>
          <div className="form-row">
            <FormGroup label="Tipo" required>
              <Select value={form.maintenance_type} onChange={e => setForm({...form, maintenance_type: e.target.value})}>
                <option value="preventiva">Preventiva</option>
                <option value="corretiva">Corretiva</option>
                <option value="higienizacao">Higienização</option>
              </Select>
            </FormGroup>
            <FormGroup label="Data Agendada">
              <Input type="date" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} />
            </FormGroup>
          </div>
          <div className="form-row">
            <FormGroup label="Responsável">
              <Input value={form.responsible} onChange={e => setForm({...form, responsible: e.target.value})} />
            </FormGroup>
            <FormGroup label="Custo (R$)">
              <Input type="number" step="0.01" value={form.cost} onChange={e => setForm({...form, cost: e.target.value})} />
            </FormGroup>
          </div>
          <FormGroup label="Descrição">
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </FormGroup>
        </form>
      </Modal>
    </>
  );
}

// ─── Financial ────────────────────────────────────────────────────────────────

export function Financial() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ financial_type: 'receita', description: '', amount: '', due_date: '', category: '', client_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientService.list({ limit: 500 }).then(r => setClients(r.data));
    load();
  }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (typeFilter) params.financial_type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    financialService.list({ ...params, limit: 300 }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [typeFilter, statusFilter]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await financialService.create(form);
      toast.success('Lançamento criado!');
      setModalOpen(false);
      load();
    } catch (err) { toast.error('Erro'); } finally { setSaving(false); }
  };

  const pay = async (id) => {
    await financialService.pay(id);
    toast.success('Pagamento registrado!');
    load();
  };

  const totals = items.reduce((acc, i) => {
    if (i.financial_type === 'receita') acc.receita += Number(i.amount);
    else acc.despesa += Number(i.amount);
    return acc;
  }, { receita: 0, despesa: 0 });

  return (
    <>
      <PageHeader title="Financeiro">
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <button className="btn btn-secondary btn-sm" onClick={async () => { try { await reportsService.financialPDF(); toast.success('PDF gerado!'); } catch { toast.error('Erro ao gerar PDF'); } }}>📄 PDF</button>
          <button className="btn btn-secondary btn-sm" onClick={async () => { try { await reportsService.financialExcel(); toast.success('Excel gerado!'); } catch { toast.error('Erro ao gerar Excel'); } }}>📊 Excel</button>
        </div>
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Lançamento</button>
      </PageHeader>
      <div className="page-content">
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>💰</div>
            <div><div className="stat-value" style={{ fontSize: 18 }}>{fmtMoney(totals.receita)}</div><div className="stat-label">Total Receitas (filtro)</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>💸</div>
            <div><div className="stat-value" style={{ fontSize: 18 }}>{fmtMoney(totals.despesa)}</div><div className="stat-label">Total Despesas (filtro)</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>📊</div>
            <div><div className="stat-value" style={{ fontSize: 18, color: (totals.receita - totals.despesa) >= 0 ? 'var(--success)' : 'var(--danger)' }}>{fmtMoney(totals.receita - totals.despesa)}</div><div className="stat-label">Saldo</div></div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="filters-bar" style={{ margin: 0 }}>
              {['', 'receita', 'despesa'].map(t => (
                <button key={t} className={`btn btn-sm ${typeFilter === t ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setTypeFilter(t)}>{t ? lbl(t) : 'Todos'}</button>
              ))}
              <span style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: 8 }} />
              {['', 'pendente', 'pago', 'atrasado'].map(s => (
                <button key={s} className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setStatusFilter(s)}>{s ? lbl(s) : 'Todos Status'}</button>
              ))}
            </div>
          </div>
          <Table headers={['Tipo','Descrição','Categoria','Vencimento','Valor','Status','Ações']} loading={loading}>
            {items.map(i => (
              <tr key={i.id}>
                <td><Badge status={i.financial_type} label={lbl(i.financial_type)} /></td>
                <td>{i.description}</td>
                <td>{i.category || '-'}</td>
                <td style={{ color: i.status === 'pendente' && new Date(i.due_date) < new Date() ? 'var(--danger)' : undefined }}>
                  {fmtDate(i.due_date)}
                </td>
                <td><strong>{fmtMoney(i.amount)}</strong></td>
                <td><Badge status={i.status} label={lbl(i.status)} /></td>
                <td>
                  {i.status === 'pendente' && (
                    <button className="btn btn-sm btn-success" onClick={() => pay(i.id)}>Pagar</button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Novo Lançamento"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
        </>}>
        <form onSubmit={save}>
          <div className="form-row">
            <FormGroup label="Tipo" required>
              <Select value={form.financial_type} onChange={e => setForm({...form, financial_type: e.target.value})}>
                <option value="receita">Receita</option>
                <option value="despesa">Despesa</option>
              </Select>
            </FormGroup>
            <FormGroup label="Cliente">
              <Select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})}>
                <option value="">Selecione...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </FormGroup>
          </div>
          <FormGroup label="Descrição" required>
            <Input required value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          </FormGroup>
          <div className="form-row">
            <FormGroup label="Valor (R$)" required>
              <Input type="number" step="0.01" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} />
            </FormGroup>
            <FormGroup label="Vencimento" required>
              <Input type="date" required value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} />
            </FormGroup>
          </div>
          <FormGroup label="Categoria">
            <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Ex: Locação, Combustível..." />
          </FormGroup>
        </form>
      </Modal>
    </>
  );
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export function Invoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [nfeModalOpen, setNfeModalOpen] = useState(false);
  const [selectedNfe, setSelectedNfe] = useState(null);
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState({ client_id: '', contract_id: '', amount: '', service_description: '', competence_date: '', iss_rate: '2' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientService.list({ limit: 500 }).then(r => setClients(Array.isArray(r)?r:r.data??[]));
    contractService.list({ limit: 500 }).then(r => setContracts(Array.isArray(r)?r:r.data??[]));
    load();
  }, []);

  const load = () => {
    setLoading(true);
    invoiceService.list({ limit: 200 }).then(r => setItems(Array.isArray(r)?r:r.data??[])).finally(() => setLoading(false));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await invoiceService.create(form);
      toast.success('NF criada!');
      setModalOpen(false);
      load();
    } catch { toast.error('Erro'); } finally { setSaving(false); }
  };

  const emit = async (inv) => {
    // Open NF-e pre-fill modal instead of direct emit
    setSelectedNfe(inv);
    setNfeModalOpen(true);
  };

  const confirmEmit = async (id) => {
    try {
      await invoiceService.emit(id);
      toast.success('NF marcada como emitida!');
      setNfeModalOpen(false);
      load();
    } catch { toast.error('Erro ao emitir'); }
  };

  const cancel = async (id) => {
    if (!window.confirm('Cancelar esta nota fiscal?')) return;
    try {
      await invoiceService.cancel(id, 'Cancelamento solicitado pelo usuário');
      toast.success('NF cancelada');
      load();
    } catch { toast.error('Erro ao cancelar'); }
  };

  // Auto-fill from contract
  const handleContractChange = (contract_id) => {
    const c = contracts.find(x => x.id === contract_id);
    if (c) {
      setForm(f => ({
        ...f, contract_id,
        client_id: c.client_id || f.client_id,
        amount: c.unit_value || f.amount,
        service_description: `Locação de banheiros químicos - Contrato ${c.number || c.contract_number || ''}`.trim(),
        competence_date: new Date().toISOString().slice(0,10),
      }));
    } else {
      setForm(f => ({ ...f, contract_id }));
    }
  };

  const getClient = (id) => clients.find(c => c.id === id);

  return (
    <>
      <PageHeader title="Notas Fiscais">
        <button className="btn btn-primary" onClick={() => setModalOpen(true)}><Plus size={16} /> Nova NF</button>
      </PageHeader>
      <div className="page-content">
        <div className="card">
          <Table headers={['Número','Cliente','Emissão','Valor','Status','Ações']} loading={loading}>
            {items.map(i => (
              <tr key={i.id}>
                <td><strong>{i.invoice_number}</strong></td>
                <td>{clients.find(c => c.id === i.client_id)?.name || '-'}</td>
                <td>{fmtDate(i.issue_date)}</td>
                <td>{fmtMoney(i.amount)}</td>
                <td><Badge status={i.status} label={lbl(i.status)} /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {i.status === 'pendente' && (
                      <button className="btn btn-sm btn-primary" onClick={() => emit(i)}>📋 Emitir NF-e</button>
                    )}
                    {i.status === 'emitida' && (
                      <button className="btn btn-sm btn-secondary" onClick={() => cancel(i.id)}>Cancelar</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Nova Nota Fiscal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Criar'}</button>
        </>}>
        <form onSubmit={save}>
          <FormGroup label="Contrato (opcional — preenche automaticamente)">
            <Select value={form.contract_id} onChange={e => handleContractChange(e.target.value)}>
              <option value="">Selecione um contrato...</option>
              {contracts.map(c => <option key={c.id} value={c.id}>{c.number || c.contract_number} — {getClient(c.client_id)?.name || 'Cliente'}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Cliente" required>
            <Select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required>
              <option value="">Selecione...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormGroup>
          <div className="form-row">
            <FormGroup label="Valor (R$)" required>
              <Input type="number" step="0.01" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} />
            </FormGroup>
            <FormGroup label="Competência">
              <Input type="date" value={form.competence_date} onChange={e => setForm({...form, competence_date: e.target.value})} />
            </FormGroup>
          </div>
          <FormGroup label="Descrição do Serviço">
            <Textarea value={form.service_description} onChange={e => setForm({...form, service_description: e.target.value})} placeholder="Locação de banheiros químicos conforme contrato..." />
          </FormGroup>
        </form>
      </Modal>

      {/* NF-e Pre-fill Modal */}
      {selectedNfe && (
        <Modal isOpen={nfeModalOpen} onClose={() => setNfeModalOpen(false)} title="Emitir NF-e — Portal SP" size="lg">
          <div style={{ background: '#fef9c3', border: '1px solid #f59e0b', borderRadius: 8, padding: '1rem', marginBottom: '1.25rem', fontSize: '.875rem' }}>
            📋 <strong>Copie os dados abaixo</strong> e preencha no Portal NF-e da Prefeitura de São Paulo:<br/>
            <a href="https://nfe.prefeitura.sp.gov.br" target="_blank" rel="noopener noreferrer"
              style={{ color: '#1e40af', fontWeight: 600 }}>
              🔗 nfe.prefeitura.sp.gov.br
            </a>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '.875rem' }}>
            {[
              ['Número NF', selectedNfe.invoice_number || '—'],
              ['Data de Emissão', new Date().toLocaleDateString('pt-BR')],
              ['Valor do Serviço', fmtMoney(selectedNfe.amount)],
              ['ISS Retido', selectedNfe.iss_retained ? 'Sim' : 'Não'],
              ['Alíquota ISS', '2%'],
              ['Código do Serviço', '7.09 — Locação de bens móveis'],
              ['CNAE', '7739-0/99 — Locação de outros equipamentos'],
              ['Local de Prestação', 'São Paulo/SP'],
            ].map(([label, value]) => (
              <div key={label} style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '.75rem' }}>
                <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '.25rem' }}>{label}</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
                <button style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '.75rem', padding: 0, marginTop: '.25rem' }}
                  onClick={() => { navigator.clipboard.writeText(value); toast.success('Copiado!'); }}>
                  📋 Copiar
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1rem', background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem' }}>
            <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '.25rem' }}>DESCRIÇÃO DO SERVIÇO</div>
            <div style={{ fontSize: '.875rem', marginBottom: '.5rem' }}>
              {selectedNfe.service_description || `Locação de banheiros químicos portáteis, incluindo entrega, coleta e higienização. Competência: ${new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}.`}
            </div>
            <button style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '.75rem', padding: 0 }}
              onClick={() => { navigator.clipboard.writeText(selectedNfe.service_description || 'Locação de banheiros químicos portáteis'); toast.success('Copiado!'); }}>
              📋 Copiar descrição
            </button>
          </div>

          {getClient(selectedNfe.client_id) && (
            <div style={{ marginTop: '1rem', background: 'var(--bg-secondary)', borderRadius: 8, padding: '1rem' }}>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '.5rem' }}>DADOS DO TOMADOR</div>
              {[
                ['Nome/Razão Social', getClient(selectedNfe.client_id)?.name],
                ['CPF/CNPJ', getClient(selectedNfe.client_id)?.document],
                ['E-mail', getClient(selectedNfe.client_id)?.email],
              ].filter(([,v]) => v).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '.3rem 0', borderBottom: '1px solid var(--border)', fontSize: '.85rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}:</span>
                  <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                    <strong>{value}</strong>
                    <button style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '.75rem' }}
                      onClick={() => { navigator.clipboard.writeText(value); toast.success('Copiado!'); }}>📋</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="modal-footer" style={{ marginTop: '1.25rem' }}>
            <button className="btn btn-secondary" onClick={() => setNfeModalOpen(false)}>Fechar</button>
            <a href="https://nfe.prefeitura.sp.gov.br" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              🔗 Abrir Portal NF-e SP
            </a>
            <button className="btn btn-success" onClick={() => confirmEmit(selectedNfe.id)}>
              ✅ Marcar como Emitida
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── Documents (simplified) ───────────────────────────────────────────────────

export function Documents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { documentService } = await import('../services/api');
    documentService.list({ limit: 200 }).then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const { documentService } = await import('../services/api');
    try {
      await documentService.upload(file, { entity_type: 'general', entity_id: 'none', document_type: 'outro' });
      toast.success('Arquivo enviado!');
      load();
    } catch { toast.error('Erro ao enviar'); } finally { setUploading(false); }
  };

  return (
    <>
      <PageHeader title="Documentos">
        <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
          <Plus size={16} /> {uploading ? 'Enviando...' : 'Enviar Arquivo'}
          <input type="file" style={{ display: 'none' }} onChange={upload} disabled={uploading} />
        </label>
      </PageHeader>
      <div className="page-content">
        <div className="card">
          <Table headers={['Nome','Tipo','Entidade','Tamanho','Vencimento','Data Upload']} loading={loading}>
            {items.map(i => (
              <tr key={i.id}>
                <td><a href={i.file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{i.name}</a></td>
                <td>{i.document_type}</td>
                <td>{i.entity_type || '-'}</td>
                <td>{i.file_size ? `${(i.file_size/1024).toFixed(0)} KB` : '-'}</td>
                <td style={{ color: i.expiry_date && new Date(i.expiry_date) < new Date() ? 'var(--danger)' : undefined }}>
                  {fmtDate(i.expiry_date)}
                </td>
                <td>{fmtDate(i.created_at)}</td>
              </tr>
            ))}
          </Table>
        </div>
      </div>
    </>
  );
}
